import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  createScheduledMessage,
  getBulkMessageStatus,
  startNewConversation,
} from 'src/models/conversation/conversation';
import { normalizeMessage } from 'src/models/conversation/normalizers';
import { formatListDateAndTime } from 'src/utils/dateFormatter';
import toast from 'src/utils/toast';

import type {
  DeliveryMethod,
  MediaUpload,
  Recipient,
} from 'src/models/conversation/_types';

export const useNewConversation = (
  afterSendCallback: (() => void) | null = null
) => {
  const navigate = useNavigate();
  const [toUsers, setToUsers] = useState<Recipient[]>([]);
  const queryClient = useQueryClient();
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'messages.newMessage',
  });
  const { t } = useTranslation('translation');

  interface ScheduledMessageData {
    newMessage: string;
    files: MediaUpload[];
    sendAt: Date;
    delivery?: DeliveryMethod[];
    subject?: string;
  }

  const sendScheduledMessageMutation = useMutation({
    mutationFn: ({
      newMessage,
      files,
      sendAt,
      delivery,
      subject,
    }: ScheduledMessageData) => {
      if (toUsers.length === 0) {
        throw new Error(tMessages('noRecipientsSelected'));
      }
      return createScheduledMessage({
        receiverId: toUsers.map((user) => user.id),
        body: newMessage,
        deliverAt: sendAt,
        files,
        delivery,
        subject,
      });
    },
    onMutate: () => {
      toast.loading(tMessages('schedulingMessage'));
    },
    onSuccess(_, variables) {
      toast.dismiss();
      toast.success(
        tMessages('scheduledMessage', {
          date: formatListDateAndTime(variables.sendAt),
        })
      );
      if (afterSendCallback) afterSendCallback();
    },
  });

  interface MessageData {
    message: string;
    files: MediaUpload[];
    delivery?: DeliveryMethod[];
    subject?: string;
  }

  const sendMessageMutation = useMutation({
    mutationFn: ({ message, files, delivery, subject }: MessageData) => {
      if (toUsers.length == 0) {
        throw new Error(tMessages('userMissing'));
      }

      return startNewConversation({
        message,
        userIds: toUsers.map((user) => user.id),
        files,
        delivery,
        subject,
      });
    },
    onMutate: () => {
      toast.loading(tMessages('sendingMessage'));
    },
    onSuccess(data) {
      toast.dismiss();
      if (
        toUsers.length == 1 &&
        (toUsers[0]?.type == 'User' || toUsers[0]?.type == 'Trainer')
      ) {
        toast.success(
          () => (
            <Stack direction="row" spacing={1} alignItems="center">
              {tMessages('messageSent')}{' '}
              <Typography
                color="white"
                component={Link}
                onClick={() => toast.dismiss()}
                to={`/messages/${data.conversationId}`}
              >
                {tMessages('viewConversation')}
              </Typography>
            </Stack>
          ),
          {
            duration: 6000,
          }
        );
        if (data.smsDeliveryBlocked) {
          toast(t('messages.smsCountryNotSupported'), { duration: 8000 });
        }
        const message = normalizeMessage(data);
        if (!afterSendCallback) {
          navigate(`/messages/${message.conversationId}`, { replace: true });
        }
      } else {
        pollBulkMessageStatus(data.id.toString());
        if (!afterSendCallback) {
          navigate('/messages/', { replace: true });
        }
      }
      if (afterSendCallback) afterSendCallback();
      queryClient.invalidateQueries({
        queryKey: ['conversations'],
      });
    },
  });

  const pollBulkMessageStatus = async (bulkMessageId: string) => {
    let completed = false;
    let attempts = 0;
    const maxAttempts = 12; // Stop after 1 minute (30 * 2 seconds)

    while (!completed && attempts < maxAttempts) {
      try {
        const status = await getBulkMessageStatus(bulkMessageId);
        toast.dismiss();
        if (status.totalCount === null) {
          toast.loading(tMessages('scheduledBulkMessage'));
        } else {
          toast.loading(
            tMessages('sentProgress', {
              sent: status.sentCount,
              total: status.totalCount,
            })
          );
        }
        if (status.sentCount === status.totalCount) {
          completed = true;
          toast.dismiss();
          toast.success(tMessages('allBulkMessagesSent'));
        } else {
          await new Promise((resolve) => setTimeout(resolve, 5000)); // Poll every 2 seconds
        }
      } catch (error) {
        toast.dismiss();
        toast.error(tMessages('bulkMessageSendError'));
        break;
      }
      attempts++;
    }

    if (!completed) {
      toast.dismiss();
      toast.error(tMessages('messageSendingConfirmation'));
    }
  };

  const sendMessage = async (options: {
    newMessage: string;
    files: MediaUpload[];
    sendAt?: Date | null;
    delivery?: DeliveryMethod[];
    subject?: string;
  }) => {
    const { newMessage, files, sendAt, delivery, subject } = options;
    if (sendAt) {
      sendScheduledMessageMutation.mutate({
        newMessage,
        files,
        sendAt,
        delivery,
        subject,
      });
    } else {
      sendMessageMutation.mutate({
        message: newMessage,
        files,
        delivery,
        subject,
      });
    }
  };

  const [scheduledMessageTime, setScheduledMessageTime] = useState(null);
  const handleScheduleMessage = (deliverAt) => {
    setScheduledMessageTime(deliverAt);
  };

  return {
    toUsers,
    setToUsers,
    sendMessage,
    handleScheduleMessage,
    scheduledMessageTime,
  };
};
